import type { DevLoginResponse } from "@shared/types";
import { apiRequest } from "../api";
import { logger } from "../logger";
import { clearIdleLock, idleLocked, writeLastActivity } from "./sessionSync";
import type { AuthAccount, ReauthOutcome, TokenProvider } from "./types";

/**
 * Dev-only token provider (LOCAL-DEV §3).
 *
 * `GET /dev/login?as=<email>` mints a token from the same in-process issuer the
 * route tests use, so every screen, header and permission check runs the real
 * code path with a real signature — only the key source differs. The route
 * module is loaded on the server only when `DEV_AUTH=1`, `Dockerfile.web` never
 * sets it, and a CI test asserts the production bundle does not contain the
 * string `dev-login`.
 *
 * The token lives in sessionStorage so it dies with the tab, matching SEC-14.
 */
const TOKEN_KEY = "skydata-dev-token";
const ACCOUNT_KEY = "skydata-dev-account";

/**
 * The role the Playwright walkthrough signs in as (LOCAL-DEV §4 step 2). Its
 * button carries the bare `dev-sign-in` test id the spec looks for.
 */
export const WALKTHROUGH_PERSONA_ROLE = "InstanceAdmin";

/** The four seeded personas, one per role (LOCAL-DEV §2, `npm run seed:dev`). */
export const DEV_PERSONAS: ReadonlyArray<{ email: string; label: string; role: string }> = [
  { email: "platformadmin@skydata.local", label: "Platform admin", role: "PlatformAdmin" },
  { email: "tenantadmin@skydata.local", label: "Tenant admin", role: "TenantAdmin" },
  { email: "instanceadmin@skydata.local", label: "Instance admin", role: "InstanceAdmin" },
  { email: "viewer@skydata.local", label: "Viewer", role: "Viewer" },
];

function readStored<T>(key: string): T | null {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeStored(key: string, value: unknown): void {
  try {
    if (value === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* private mode — the session simply won't survive a reload */
  }
}

export class DevTokenProvider implements TokenProvider {
  readonly kind = "dev" as const;

  private token: string | null = readStored<string>(TOKEN_KEY);
  private currentAccount: AuthAccount | null = readStored<AuthAccount>(ACCOUNT_KEY);

  /** Notified when the session changes, so React can re-render. */
  constructor(private readonly onChange: () => void) {}

  get account(): AuthAccount | null {
    return this.currentAccount;
  }

  async getAccessToken(): Promise<string | null> {
    if (idleLocked()) return null;
    return this.token;
  }

  async signIn(hint?: string): Promise<void> {
    const email = hint ?? DEV_PERSONAS[0]!.email;
    const data = await apiRequest<DevLoginResponse>(`/dev/login?as=${encodeURIComponent(email)}`, {
      anonymous: true,
    });
    this.token = data.token;
    this.currentAccount = {
      email: data.user.email,
      name: data.user.displayName || data.user.email.split("@")[0] || data.user.email,
    };
    writeStored(TOKEN_KEY, this.token);
    writeStored(ACCOUNT_KEY, this.currentAccount);
    // A sign-in is activity for the shared idle clock (review r9 #1).
    clearIdleLock();
    writeLastActivity();
    logger.info("dev sign-in completed");
    this.onChange();
  }

  /**
   * FR-14: re-mint the token for the SAME account.
   *
   * The dev issuer stamps `auth_time` when it mints, so asking it again is
   * exactly what a fresh interactive sign-in gives the real provider — and the
   * account never changes, so the screen that asked keeps its instance, its
   * permissions and its form.
   */
  async reauthenticate(): Promise<ReauthOutcome> {
    const email = this.currentAccount?.email;
    if (!email) return "failed";
    await this.signIn(email);
    return this.token ? "confirmed" : "failed";
  }

  async signOut(): Promise<void> {
    this.token = null;
    this.currentAccount = null;
    writeStored(TOKEN_KEY, null);
    writeStored(ACCOUNT_KEY, null);
    this.onChange();
  }
}
